import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import Logo from "../images/logoss.png";
import "../styles/verify.css";
import { FaClock, FaSpinner } from "react-icons/fa"; // Add spinner icon

const VerifyAccount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp, resendOtp } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(Array(6).fill("")); // OTP input as an array of 6 empty strings
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // Loading state for OTP request
  const [isVerifying, setIsVerifying] = useState(false); // Loading state for OTP verification

  // Create refs for each OTP input field
  const inputRefs = useRef([]);

  const hasSentInitialOtp = useRef(false);

useEffect(() => {
  const params = new URLSearchParams(location.search);
  const emailFromParams = params.get("email");
  const alreadySent = params.get("alreadySent"); // from signup

  if (emailFromParams) {
    setEmail(emailFromParams);

    if (!hasSentInitialOtp.current && !alreadySent) {
      setResendCooldown(30);
      hasSentInitialOtp.current = true;

      const sendInitialOtp = async () => {
        setIsLoading(true);
        const res = await resendOtp(emailFromParams);
        setIsLoading(false);

        if (res.error) {
          setError(res.error);
          setTimeout(() => setError(null), 3000);
        } else {
          setMessage("📨 OTP sent to your email.");
        }
      };

      sendInitialOtp();
    }
  }
}, [location.search]);



  // ✅ Countdown timer for resend OTP
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // ✅ Submit OTP
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    const otpCode = otp.join(""); // Join OTP array into a single string

    if (!otpCode || otpCode.length < 6) {
      return setError("Please enter a valid 6-digit OTP.");
    }

    console.log("🔐 Verifying OTP...");
    setIsVerifying(true); // Set verifying state to true

    const res = await verifyOtp(email, otpCode);

    setIsVerifying(false); // Reset verifying state

    if (res.error) {
      setError(res.error);

      // Automatically clear the error and reset the OTP input after 3 seconds
      setTimeout(() => {
        setError(null); // Clear the error
        setOtp(Array(6).fill("")); // Reset the OTP input fields
      }, 3000);
    } else {
      setMessage("✅ Verification successful!");
      setTimeout(() => navigate("/login"), 1500);
    }
  };

  // ✅ Resend OTP (only when the user clicks "Resend Code")
  const handleResend = async () => {
    if (resendCooldown > 0 || isLoading) return; // Disable if cooldown or in progress

    setError(null);
    setMessage(null);
    setIsLoading(true); // Set loading state

    console.log("🔁 Resending OTP...");
    const res = await resendOtp(email);

    setIsLoading(false); // Reset loading state once request is complete

    if (res.error) {
      setError(res.error);

      // Automatically clear the error and reset the OTP input after 3 seconds
      setTimeout(() => {
        setError(null); // Clear the error
        setOtp(Array(6).fill("")); // Reset the OTP input fields
      }, 3000);
    } else {
      setMessage("📨 OTP resent to your email.");
      setResendCooldown(30); // 30s cooldown
    }
  };

  // Handle OTP input change
  const handleOtpChange = (e, index) => {
    const value = e.target.value;

    // Ensure the value is a digit and has length of 1 (to avoid accidental multi-digit input)
    if (/[^0-9]/.test(value) || value.length > 1) {
      return; // If input is not a number or has more than one digit, don't update
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Focus next input if it exists
    if (index < otp.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        // If current box is not empty, clear it
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // If current box is empty, go to previous box
        inputRefs.current[index - 1].focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < otp.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };


  // Check if all OTP fields are filled for enabling the submit button
  const isFormValid = otp.every((digit) => digit.length === 1);

  return (
    <div className=" login-container">
      {/* Left side background like Login */}
      <div className="login-left">
        <div className="login-left-content">
          <h2>SubnetExpress</h2>
          <p>Secure your access to fast, affordable digital services today.</p>
        </div>
      </div>

      {/* Right side OTP verification box */}
      <div className="login-right">
        <div className="login-box verify-box">
          <img src={Logo} alt="Logo" className="login-logo" />
          <h2 className="login-title">Verify Your Account</h2>
          <p className="login-subtext">
            We’ve sent a 6-digit code to your email. Enter it to verify.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="verify-otp-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}  // ✅ Add this
                  required
                  ref={(el) => (inputRefs.current[index] = el)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              ))}
            </div>

            <button className="verify-button" type="submit" disabled={!isFormValid || isVerifying}>
              {isVerifying ? <FaSpinner className="spinner" /> : "Verify"}
            </button>
          </form>

          <div className="resend-container">
            {resendCooldown > 0 ? (
              <p className="resend-wait">
                You can request a new code in <FaClock /> {resendCooldown}s
              </p>
            ) : (
              <p className="resend-line">
                <span className = "login-subtext">Didn’t receive the code?</span>{" "}
                <span className="resend-link" onClick={handleResend}>
                  {isLoading ? (
                    <FaSpinner className="spinner" />
                  ) : (
                    "Request new code"
                  )}
                </span>
              </p>
            )}
          </div>


          {error && <p className="error-text text-center">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default VerifyAccount;
